/**
 * Web-side wrappers around the realtime Backlog search callables.
 *
 * - `searchBacklogIssues` — fetches the candidate list the inbox renders.
 * - `listBacklogIssueMeta` — fetches the dropdown master data the filter
 *   chips need (statuses / users / categories / milestones / priorities).
 *
 * Both are factored as `_runner` modules so vitest can swap in a mock
 * without going through `firebase/functions` (mirrors the pattern in
 * `suggestion.ts`).
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { SuggestionCandidate, SuggestionKind, SuggestionStatus } from '@mct/shared';
import { getFirebaseApp } from './firebase';

export interface SearchBacklogIssuesFilter {
  statusIds?: number[];
  assigneeIds?: number[];
  categoryIds?: number[];
  milestoneIds?: number[];
  priorityIds?: number[];
  /** Subset of configured Backlog project keys. Server intersects with config. */
  backlogProjectKeys?: string[];
  keyword?: string;
  updatedSinceIso?: string;
  count?: number;
}

export interface SearchBacklogIssuesBody {
  projectId: string;
  filter?: SearchBacklogIssuesFilter;
}

export interface ListBacklogIssueMetaFilter {
  statusIds?: number[];
  assigneeIds?: number[];
  categoryIds?: number[];
  milestoneIds?: number[];
  priorityIds?: number[];
  backlogProjectKeys?: string[];
}

export interface BacklogSearchResultItem {
  issueKey: string;
  backlogProjectKey: string;
  issueSummary: string;
  issueUrl: string;
  issueStatus: string;
  issueStatusId: number | null;
  assigneeId: string | null;
  assigneeName: string | null;
  issueTypeId: number | null;
  issueTypeName: string | null;
  categoryNames: string[];
  milestoneNames: string[];
  priorityId: number | null;
  priorityName: string | null;
  dueDate: string | null;
  issueCreatedAt: string | null;
  updated: string | null;
  kind: SuggestionKind;
  candidates: SuggestionCandidate[];
  candidateWbsItemIds: string[];
  actionedState: SuggestionStatus;
  actionedStateMeta: {
    snoozedUntil: string | null;
    snoozeReason: string | null;
    acceptedExecutionId: string | null;
    acceptedWbsItemId: string | null;
    actorUserId: string | null;
  } | null;
}

/** Backlog project key→name pair surfaced by both callables. */
export interface BacklogProjectMeta {
  key: string;
  name: string;
}

export type SearchBacklogIssuesResult =
  | {
      ok: true;
      results: BacklogSearchResultItem[];
      backlogProjects: BacklogProjectMeta[];
    }
  | {
      ok: false;
      reason: 'config-missing' | 'secret-error' | 'backlog-error';
      message: string;
    };

export interface ListBacklogIssueMetaBody {
  projectId: string;
  filter?: ListBacklogIssueMetaFilter;
}

export interface BacklogStatusMeta {
  id: number;
  name: string;
  backlogProjectKey: string;
}
export interface BacklogUserMeta {
  id: number;
  name: string;
  backlogProjectKey: string;
}
export interface BacklogCategoryMeta {
  id: number;
  name: string;
  backlogProjectKey: string;
}
export interface BacklogMilestoneMeta {
  id: number;
  name: string;
  backlogProjectKey: string;
  archived: boolean;
}
export interface BacklogPriorityMeta {
  id: number;
  name: string;
}

export type ListBacklogIssueMetaResult =
  | {
      ok: true;
      statuses: BacklogStatusMeta[];
      users: BacklogUserMeta[];
      categories: BacklogCategoryMeta[];
      milestones: BacklogMilestoneMeta[];
      priorities: BacklogPriorityMeta[];
      backlogProjects: BacklogProjectMeta[];
    }
  | {
      ok: false;
      reason: 'config-missing' | 'secret-error' | 'backlog-error';
      message: string;
    };

export type SearchRunner = (body: SearchBacklogIssuesBody) => Promise<SearchBacklogIssuesResult>;
export type MetaRunner = (body: ListBacklogIssueMetaBody) => Promise<ListBacklogIssueMetaResult>;

let _searchRunner: SearchRunner | null = null;
let _metaRunner: MetaRunner | null = null;

/** Test seam — swap in a mock runner. Returns the previous runner. */
export function setSearchRunnerForTests(runner: SearchRunner | null): SearchRunner | null {
  const prev = _searchRunner;
  _searchRunner = runner;
  return prev;
}

export function setMetaRunnerForTests(runner: MetaRunner | null): MetaRunner | null {
  const prev = _metaRunner;
  _metaRunner = runner;
  return prev;
}

function defaultSearchRunner(): SearchRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<SearchBacklogIssuesBody, SearchBacklogIssuesResult>(
      fns,
      'searchBacklogIssues',
    );
    const res = await callable(body);
    return res.data;
  };
}

function defaultMetaRunner(): MetaRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<ListBacklogIssueMetaBody, ListBacklogIssueMetaResult>(
      fns,
      'listBacklogIssueMeta',
    );
    const res = await callable(body);
    return res.data;
  };
}

export async function searchBacklogIssues(
  body: SearchBacklogIssuesBody,
): Promise<SearchBacklogIssuesResult> {
  const run = _searchRunner ?? defaultSearchRunner();
  return await run(body);
}

export async function listBacklogIssueMeta(
  body: ListBacklogIssueMetaBody,
): Promise<ListBacklogIssueMetaResult> {
  const run = _metaRunner ?? defaultMetaRunner();
  return await run(body);
}
